import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, ArrowClockwise, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 25 }, (_, h) => h);
const hh = (h) => `${String(h % 24).padStart(2, "0")}:00${h === 24 ? " (midnight)" : ""}`;

export function SocBusinessHours({ clients }) {
  const [bh, setBh] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recalc, setRecalc] = useState(false);
  const load = () => api.get("/soc/business-hours").then(r => setBh(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!bh) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const setProfile = (i, patch) => setBh({ ...bh, profiles: bh.profiles.map((p, j) => j === i ? { ...p, ...patch } : p) });
  const toggleDay = (i, d) => { const p = bh.profiles[i]; const days = p.work_days.includes(d) ? p.work_days.filter(x => x !== d) : [...p.work_days, d].sort(); setProfile(i, { work_days: days }); };
  const addProfile = () => setBh({ ...bh, profiles: [...bh.profiles, { id: `p_${Date.now()}`, name: "New profile", always: false, start_hour: 9, end_hour: 18, work_days: [0, 1, 2, 3, 4], use_holidays: true }] });
  const removeProfile = (i) => {
    const p = bh.profiles[i];
    if (bh.profiles.length === 1) return toast.error("Keep at least one profile");
    const map = Object.fromEntries(Object.entries(bh.client_map).filter(([, v]) => v !== p.id));
    setBh({ ...bh, profiles: bh.profiles.filter((_, j) => j !== i), client_map: map, default_profile_id: bh.default_profile_id === p.id ? bh.profiles.find((_, j) => j !== i).id : bh.default_profile_id });
  };
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/soc/business-hours", bh); setBh(data); toast.success("Business hours saved — applies to new cases and future severity/client changes"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const recalculate = async () => {
    setRecalc(true);
    try { const { data } = await api.post("/soc/business-hours/recalculate"); toast.success(`SLA due dates recalculated for ${data.updated} open case(s)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Recalculation failed"); }
    finally { setRecalc(false); }
  };
  const names = Object.fromEntries(bh.profiles.map(p => [p.id, p.name]));
  const clientNames = [...new Set([...(clients || []).map(c => c.name), ...Object.keys(bh.client_map)])].sort();

  return (
    <div className="space-y-5" data-testid="soc-business-hours">
      <Card className="p-5 border-border rounded-sm">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Business-hours profiles</div>
            <p className="text-xs text-muted-foreground mt-1">SLA response/resolution clocks only run inside the profile's hours (IST). Holidays come from the Timesheet Holiday Calendar lists flagged for SLA.</p>
          </div>
          <Button data-testid="soc-bh-add" variant="outline" size="sm" onClick={addProfile} className="rounded-sm font-mono text-[10px] uppercase"><Plus size={13} className="mr-1" /> Profile</Button>
        </div>
        <div className="space-y-3 mt-4">
          {bh.profiles.map((p, i) => (
            <div key={p.id} data-testid={`soc-bh-profile-${p.id}`} className="border border-border rounded-sm p-3 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Input data-testid={`soc-bh-name-${p.id}`} value={p.name} onChange={e => setProfile(i, { name: e.target.value })} className="rounded-sm h-8 text-sm max-w-xs" />
                <label className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground"><Switch data-testid={`soc-bh-always-${p.id}`} checked={!!p.always} onCheckedChange={v => setProfile(i, { always: v })} /> 24×7</label>
                {bh.default_profile_id === p.id && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary">Default</span>}
                <button data-testid={`soc-bh-remove-${p.id}`} onClick={() => removeProfile(i)} className="ml-auto text-muted-foreground hover:text-rag-red-fg" title="Remove profile"><Trash size={14} /></button>
              </div>
              {!p.always && (
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-1">
                    {DAYS.map((d, di) => (
                      <button key={d} type="button" data-testid={`soc-bh-day-${p.id}-${di}`} onClick={() => toggleDay(i, di)} aria-pressed={p.work_days.includes(di)}
                        className={`h-7 w-10 text-[10px] font-mono uppercase rounded-sm border transition-colors ${p.work_days.includes(di) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{d}</button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Clock size={14} className="text-muted-foreground" />
                    <Select value={String(p.start_hour)} onValueChange={v => setProfile(i, { start_hour: Number(v) })}>
                      <SelectTrigger data-testid={`soc-bh-start-${p.id}`} className="rounded-sm h-8 w-24 text-xs font-mono"><SelectValue /></SelectTrigger>
                      <SelectContent>{HOURS.slice(0, 24).map(h => <SelectItem key={h} value={String(h)}>{hh(h)}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-muted-foreground">to</span>
                    <Select value={String(p.end_hour)} onValueChange={v => setProfile(i, { end_hour: Number(v) })}>
                      <SelectTrigger data-testid={`soc-bh-end-${p.id}`} className="rounded-sm h-8 w-32 text-xs font-mono"><SelectValue /></SelectTrigger>
                      <SelectContent>{HOURS.slice(1).map(h => <SelectItem key={h} value={String(h)}>{hh(h)}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-[10px] font-mono text-muted-foreground">IST{p.end_hour <= p.start_hour ? " · overnight shift" : ""}</span>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch data-testid={`soc-bh-holidays-${p.id}`} checked={!!p.use_holidays} onCheckedChange={v => setProfile(i, { use_holidays: v })} /> Pause on holidays</label>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 border-border rounded-sm">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Client mapping</div>
        <p className="text-xs text-muted-foreground mb-4">Clients without an explicit profile use the default. Changing a case's client re-derives its SLA due dates.</p>
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase text-muted-foreground w-44">Default profile</span>
          <Select value={bh.default_profile_id} onValueChange={v => setBh({ ...bh, default_profile_id: v })}>
            <SelectTrigger data-testid="soc-bh-default" className="rounded-sm h-8 w-72 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{bh.profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <table className="w-full text-sm" data-testid="soc-bh-client-table">
          <thead className="text-[10px] font-mono uppercase text-muted-foreground"><tr><th className="text-left py-2">Client</th><th className="text-left py-2">Business hours</th></tr></thead>
          <tbody>
            {clientNames.map(name => (
              <tr key={name} className="border-t border-border">
                <td className="py-2 text-xs">{name}</td>
                <td className="py-2">
                  <Select value={bh.client_map[name] || "__default"} onValueChange={v => { const map = { ...bh.client_map }; if (v === "__default") delete map[name]; else map[name] = v; setBh({ ...bh, client_map: map }); }}>
                    <SelectTrigger data-testid={`soc-bh-client-${name}`} className="rounded-sm h-8 w-72 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default">Default · {names[bh.default_profile_id]}</SelectItem>
                      {bh.profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
            {clientNames.length === 0 && <tr><td colSpan={2} className="py-6 text-center text-xs text-muted-foreground">No SOC clients in the registry yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <Button data-testid="soc-bh-save" onClick={save} disabled={saving} className="rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : "Save business hours"}</Button>
        <Button data-testid="soc-bh-recalc" variant="outline" onClick={recalculate} disabled={recalc} className="rounded-sm font-mono uppercase text-xs"><ArrowClockwise size={14} className={`mr-1 ${recalc ? "animate-spin" : ""}`} /> Recalculate open cases</Button>
        <span className="text-[11px] text-muted-foreground">Recalculation re-derives due dates for all open cases from their creation time using the saved mapping.</span>
      </div>
    </div>
  );
}
